import {
  scaleLinear,
} from 'd3-scale';
import partition from 'lodash-es/partition';
import sum from 'lodash-es/sum';
import {
  ProductClass,
} from '../../graphQL/types/shared';
import { extractFromStringProductId } from '../../graphQL/Utils';
import {
  formatDistance,
  formatOpportunityGain,
  formatPCI,
  formatRCA,
  formatTradeValue,
} from '../../numberFormatters';
import {
  average,
  groupByMap,
  ILoadable,
  LoadableStatus,
  oldProductLevelToNewProductLevel,
  ProductMetadatumLevel as Level,
} from '../../Utils';
import {
  newGetDisplayedProductCode,
} from '../../Utils';
import {
  IDetailOverlayRow as IRow,
  IntervalWidener,
} from '../../viz/Utils';
import {
  IMetadatum,
} from '../fetchedData/productMetadata';
import {
  ExportStatus,
  ExportType,
  getWidendedAndNicedDomain,
  IAggregateOutput,
  IDatumWithSectionAndGroup,
  IFilteredByYearOutput,
  IProcessedNodePrecursorWithoutCoords,
  IProcessedNodePrecursorWithoutY,
  ISize,
  ValueGetter,
} from './Utils';

type GroupAggregator = (
    groupId: string,
    elems: IDatumWithSectionAndGroup[],
    metadata: Record<string, IMetadatum>,
    year: number,
    productClass: ProductClass,
  ) => IProcessedNodePrecursorWithoutCoords;

const fourDigitAggregator: GroupAggregator = (
    newGroupId: string, elems: IDatumWithSectionAndGroup[],
    metadata: Record<string, IMetadatum>, year: number, productClass: ProductClass) => {

  // Note: there's only one element in `elems`:
  const {
    worldTrade, export_rca, distance, cog, pci, color, section,
  } = elems[0];

  const oldGroupId = extractFromStringProductId(newGroupId).id;

  const retrievedMetadatum = metadata[oldGroupId];
  if (retrievedMetadatum === undefined) {
    throw new Error('No metadata for product ' + oldGroupId);
  }
  const {code, name_en, name_short_en, level: oldLevel} = retrievedMetadatum;
  const newLevel = oldProductLevelToNewProductLevel(oldLevel);
  const exportStatus: ExportStatus =
    (export_rca >= 1) ?
      {type: ExportType.WhollyExported, value: worldTrade} :
      {type: ExportType.WhollyNotExported, value: worldTrade};
  const codeInfo: IRow = {
    label: __lexiconText('applicationWide.code'),
    value: newGetDisplayedProductCode(code, productClass, newLevel),
  };
  const yearInfo: IRow = {
    label: __lexiconText('applicationWide.year'), value: year,
  };
  const worldTradeInfo: IRow = {
    label: __lexiconText('applicationWide.worldTrade'), value: formatTradeValue(worldTrade),
  };
  const rcaInfo: IRow = {
    label: __lexiconText('applicationWide.rcaFull'), value: formatRCA(export_rca),
  };
  const distanceInfo: IRow = {
    label: __lexiconText('applicationWide.distance'), value: formatDistance(distance),
  };
  const pciInfo: IRow = {
    label: __lexiconText('applicationWide.pciFull'), value: formatPCI(pci),
  };
  const opportunityGainInfo: IRow = {
    label: __lexiconText('applicationWide.opportunityGain'), value: formatOpportunityGain(cog),
  };
  const detailOverlayInfo = [
    codeInfo, yearInfo, worldTradeInfo, rcaInfo, distanceInfo, pciInfo, opportunityGainInfo,
  ];
  const output: IProcessedNodePrecursorWithoutCoords = {
    id: newGroupId,
    shortLabel: name_short_en,
    longLabel: name_en,
    code, exportStatus, color, section,
    distance,
    opportunityGain: cog,
    detailOverlayInfo,
    pci, level: newLevel,
  };
  return output;
};

const higherLevelAggregator: GroupAggregator =
  (newGroupID: string, elems: IDatumWithSectionAndGroup[], metadata: Record<string, IMetadatum>,
   year: number, productClass: ProductClass) => {

  // TODO: reconsider how `color` is obtained from the first element of `elems`.
  // because `elems` can possibly be empty?
  const {color, section} = elems[0];
  const oldGroupID = extractFromStringProductId(newGroupID).id;
  const retrievedMetadatum = metadata[oldGroupID];
  if (retrievedMetadatum === undefined) {
    throw new Error('Cannot find metadata for product ' + oldGroupID);
  }
  const {code, name_en, name_short_en, level: oldLevel} = retrievedMetadatum;
  const allDistances = elems.map(({distance}) => distance);
  const allOGs = elems.map(({cog}) => cog);
  const allRCAs = elems.map(({export_rca}) => export_rca);
  const allPCIs = elems.map(({pci}) => pci);

  // Calculate the share of exported and non-exported trade:
  const [exportedElems, unexportedElems] = partition(elems, ({export_rca}) => export_rca >= 1);
  const exportedValue = sum(exportedElems.map(({worldTrade}) => worldTrade));
  const unexportedValue = sum(unexportedElems.map(({worldTrade}) => worldTrade));
  const worldTradeSum = sum(unexportedElems.map(({worldTrade}) => worldTrade));
  const exportStatus: ExportStatus = {
    type: ExportType.Mixed,
    exportedValue, unexportedValue,
    totalValue: exportedValue + unexportedValue,
  };
  const averageRCA = average(allRCAs);
  const averagePCI = average(allPCIs);
  const averageDistance = average(allDistances);
  const averageOpportunityGain = average(allOGs);

  const newLevel = oldProductLevelToNewProductLevel(oldLevel);
  const codeInfo: IRow = {
    label: __lexiconText('applicationWide.code'),
    value: newGetDisplayedProductCode(code, productClass, newLevel),
  };
  const yearInfo: IRow = {
    label: __lexiconText('applicationWide.year'), value: year,
  };
  const worldTradeInfo: IRow = {
    label: __lexiconText('applicationWide.worldTrade'), value: formatTradeValue(worldTradeSum),
  };
  const rcaInfo: IRow = {
    label: __lexiconText('applicationWide.rcaFull'), value: formatRCA(averageRCA),
  };
  const distanceInfo: IRow = {
    label: __lexiconText('applicationWide.distance'), value: formatDistance(averageDistance),
  };
  const pciInfo: IRow = {
    label: __lexiconText('applicationWide.pciFull'), value: formatPCI(averagePCI),
  };
  const opportunityGainInfo: IRow = {
    label: __lexiconText('applicationWide.opportunityGain'), value: formatOpportunityGain(averageOpportunityGain),
  };
  const detailOverlayInfo = [
    codeInfo, yearInfo, worldTradeInfo, rcaInfo, distanceInfo, pciInfo, opportunityGainInfo,
  ];

  const output: IProcessedNodePrecursorWithoutCoords = {
    id: newGroupID,
    shortLabel: name_short_en,
    longLabel: name_en,
    code, exportStatus, color, section,
    distance: averageDistance,
    opportunityGain: averageOpportunityGain,
    detailOverlayInfo,
    pci: averagePCI,
    level: newLevel,
  };

  return output;
};

const getAggregate = (widenIntervalX: IntervalWidener) => (
    getValue: ValueGetter,
    level: Level,
    metadataStatus: ILoadable<Record<string, IMetadatum>>,
    filteredStatus: ILoadable<IFilteredByYearOutput>,
    sizeStatus: ILoadable<ISize>,
    year: number,
    productClass: ProductClass,
  ): ILoadable<IAggregateOutput> => {

  let result: ILoadable<IAggregateOutput>;
  if (filteredStatus.status === LoadableStatus.Initial ||
      metadataStatus.status === LoadableStatus.Initial ||
      sizeStatus.status === LoadableStatus.Initial) {
    result = {status: LoadableStatus.Initial};
  } else if (filteredStatus.status === LoadableStatus.Loading ||
              metadataStatus.status === LoadableStatus.Loading ||
              sizeStatus.status === LoadableStatus.Loading)  {
    result = {status: LoadableStatus.Loading};
  } else if (filteredStatus.status === LoadableStatus.NotPresent ||
              metadataStatus.status === LoadableStatus.NotPresent ||
              sizeStatus.status === LoadableStatus.NotPresent) {
    result = {status: LoadableStatus.NotPresent};
  } else {
    const {data: {
      nodes: withTrade,
      maxOpportunityGain, minOpportunityGain,
      maxDistance, minDistance,
      maxComplexity, minComplexity,
      averageComplexity, averageOpportunityGain,
    }} = filteredStatus;
    const {data: metadata} = metadataStatus;
    const {data: {width}} = sizeStatus;

    const aggregate = (level === Level.fourDigit) ? fourDigitAggregator : higherLevelAggregator;

    const grouped = groupByMap(withTrade, ({group}) => group);

    const precursorsWithoutCoords = [...grouped.entries()].map(
      ([id, groupElems]) => aggregate(id, groupElems, metadata, year, productClass),
    );

    const [xAxisMin, xAxisMax] = getWidendedAndNicedDomain(minDistance, maxDistance, widenIntervalX);

    const xScale = scaleLinear<number, number>()
                    .domain([xAxisMin, xAxisMax])
                    .range([0, width]);

    // Sort in descending order so that smaller nodes aren't blocked by larger
    // ones when rendered in the DOM:
    const unsortedPrecursors: IProcessedNodePrecursorWithoutY[] = precursorsWithoutCoords.map(elem => {
      const {distance} = elem;
      return {
        ...elem,
        x: xScale(distance),
        xValue: distance,
      };
    });

    const precursors = unsortedPrecursors.sort((a, b) => getValue(b) - getValue(a));

    result = {
      status: LoadableStatus.Present,
      data: {
        nodes: precursors,
        maxOpportunityGain, minOpportunityGain,
        maxComplexity, minComplexity,
        xAxisMin, xAxisMax, xScale,
        averageComplexity, averageOpportunityGain,
      },
    };
  }
  return result;
};

export default getAggregate;
